import {
    useRef,
    useContext,
    useLayoutEffect,
    useState,
    useMemo,
    useEffect,
} from "react";
import clsx from "clsx";
import { GraphicalViewerContext } from "../../contexts/GraphicalViewerContext";
import formatUSD from "../../utilities/formatUSD";
import formatSequential from "../../utilities/formatSequential";

const countTractsAcres = (tracts) =>
    tracts.reduce((sum, t) => sum + parseFloat(t.tract_acres), 0);

const getPpaText = (tracts, bid, threshold) => {
    const ppa = Math.round(bid / countTractsAcres(tracts));
    if (ppa < threshold) {
        return formatUSD(ppa);
    }
    return "N/A";
};

// Map text size setting to vh-based font size so the table scales with viewport height.
// Calibrated so at 1080p, "lg" ≈ 18px (18 / 1080 * 100 ≈ 1.67vh).
const getTableFontSizeVh = (textSize) => {
    switch (textSize) {
        case "lg":
            return 1.67;
        case "xl":
            return 1.85;
        case "2xl":
            return 2.22;
        case "3xl":
            return 2.78;
        case "4xl":
            return 3.33;
        default:
            return 1.67;
    }
};

const TOP_MARGIN = 12; // px
const PAGINATION_BAR = 32; // px
const ROW_PADDING_EM = 0.5; // em — scales with text size for consistent padding across resolutions

export default function GVCompletionCard() {
    const tableBodyRef = useRef(null);
    const tableRef = useRef(null);
    const allBidsRef = useRef(null);
    const {
        settings,
        completion,
        bidPage,
        bidPages,
        setBidPage,
        setBidPages,
        shouldUsePagination,
        isDragging,
    } = useContext(GraphicalViewerContext);

    const [containerWidth, setContainerWidth] = useState(0);
    const [paginatedRows, setPaginatedRows] = useState([]);
    const [containerHeight, setContainerHeight] = useState(0);
    const tableFontSize = useMemo(() => {
        return `${getTableFontSizeVh(settings?.tableTextSize || "lg")}vh`;
    }, [settings?.tableTextSize]);

    const allBids = useMemo(() => completion?.bids ?? [], [completion]);

    useLayoutEffect(() => {
        const recompute = () => {
            const header = document.getElementById("gv-header");
            const footer = document.getElementById("gv-footer");

            // Calculate containerHeight here
            const height =
                window.innerHeight -
                (header?.offsetHeight ?? 0) -
                (footer?.offsetHeight ?? 0) -
                TOP_MARGIN -
                PAGINATION_BAR;
            setContainerHeight(height);

            // Get all rendered rows in the table and sum the total height of each row and determine if they can fit within the viewport.
            // Loop through the rows and add them to the paginatedRows array, only if the total height of the rows is less than the viewport height. Key each group of rows by the page number starting with 1.
            const paginatedRowsTemp = [];
            const rows = allBidsRef.current.querySelectorAll("tr");
            const rowsArray = Array.from(rows);

            let currentPage = 1;
            let currentSubset = [];
            let currentHeight = 0;

            const smallestRowHeight = Math.min(
                ...rowsArray.map((row) => row.getBoundingClientRect().height)
            );

            for (const [index, row] of rowsArray.entries()) {
                const rowHeight = row.getBoundingClientRect().height;
                if (
                    currentHeight + rowHeight >= height - smallestRowHeight &&
                    currentSubset.length > 0
                ) {
                    paginatedRowsTemp.push({
                        page: currentPage,
                        rows: currentSubset,
                    });
                    currentPage += 1;
                    currentSubset = [];
                    currentHeight = 0;
                }

                if (allBids[index]) {
                    currentSubset.push(allBids[index]);
                    currentHeight += rowHeight;
                }
            }
            if (currentSubset.length > 0) {
                paginatedRowsTemp.push({
                    page: currentPage,
                    rows: currentSubset,
                });
            }

            setPaginatedRows(paginatedRowsTemp);
        };

        recompute(); // run once
        const ro = new ResizeObserver(recompute); // watch viewport
        ro.observe(document.documentElement);

        return () => ro.disconnect();
    }, [settings?.tableTextSize, allBids, containerWidth]);

    useLayoutEffect(() => {
        if (allBidsRef.current && isDragging) {
            const ro = new ResizeObserver(() => {
                setContainerWidth(
                    allBidsRef.current.getBoundingClientRect().width
                );
            });
            ro.observe(allBidsRef.current);

            return () => ro.disconnect();
        }
    }, [allBids, isDragging]);

    const numberOfPages = Math.ceil(paginatedRows.length || 1);

    const visibleBids = useMemo(() => {
        return paginatedRows[bidPage - 1]?.rows ?? [];
    }, [paginatedRows, bidPage]);

    useEffect(() => {
        if (bidPages !== numberOfPages) {
            setBidPages(numberOfPages);
        }
        if (bidPage > numberOfPages && numberOfPages > 0) {
            setBidPage(numberOfPages);
        }
    }, [numberOfPages, bidPage, bidPages, setBidPages, setBidPage, settings]);

    return (
        <>
            <div
                ref={tableBodyRef}
                id="gv-scroll-container"
                className="overflow-hidden relative"
                style={{ height: containerHeight, marginTop: TOP_MARGIN }}
            >
                <table
                    className="w-full divide-y divide-brand-gray-300 font-secondary"
                    ref={tableRef}
                    style={{ fontSize: tableFontSize }}
                >
                    <thead className="sticky top-0 bg-white border-b-2 border-gray-200">
                        <tr>
                            <th
                                className="font-normal text-gray-800 px-1 text-center w-1/12"
                                style={{ fontSize: "0.85em" }}
                            >
                                BIDDER
                            </th>
                            <th
                                className="font-normal text-gray-800 px-1 text-center"
                                style={{ maxWidth: "13.5em", width: "13.5em", fontSize: "0.85em" }}
                            >
                                TRACTS
                            </th>
                            <th
                                className="font-normal text-gray-800 px-1 text-center"
                                style={{ fontSize: "0.85em" }}
                            >
                                BID AMOUNT
                            </th>
                            <th
                                className="font-normal text-gray-800 px-1 text-center"
                                style={{ fontSize: "0.85em" }}
                            >
                                ACRES
                            </th>
                            {(!settings || settings.showPricePerAcre) && (
                                <th
                                    className="font-normal text-gray-800 px-1 text-center"
                                    style={{ fontSize: "0.85em" }}
                                >
                                    PPA
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="w-full bg-white divide-y divide-gray-200">
                        {visibleBids.map((data, i) => {
                            return (
                                <tr
                                    key={data.bid_id}
                                    className={clsx(
                                        "borderless",
                                        i % 2 && "bg-brand-gray-200"
                                    )}
                                >
                                    <td className="w-1/12 px-4">
                                        <div className="flex justify-between gap-2">
                                            <div className="flex items-center justify-center font-bold">
                                                #{data.bidder?.bidder_number}
                                            </div>
                                            <div
                                                style={{
                                                    width: "1em",
                                                    backgroundColor:
                                                        data.bidder
                                                            ?.bidder_color ??
                                                        "#ccc",
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td
                                        className="px-4 font-bold text-center break-words"
                                        style={{
                                            maxWidth: "13.5em",
                                            width: "13.5em",
                                            paddingTop: `${ROW_PADDING_EM}em`,
                                            paddingBottom: `${ROW_PADDING_EM}em`,
                                        }}
                                    >
                                        {data.bid_alias && (
                                            <span>{data.bid_alias} : </span>
                                        )}
                                        {formatSequential(data.bid_name)}
                                    </td>

                                    <td className="px-4 font-bold text-center">
                                        {formatUSD(data.bid_amount)}
                                    </td>
                                    <td className="px-4 font-bold text-center">
                                        {Math.round(
                                            countTractsAcres(data.tracts)
                                        )}
                                    </td>
                                    {(!settings ||
                                        settings.showPricePerAcre) && (
                                        <td className="px-4 font-bold text-center">
                                            {getPpaText(
                                                data.tracts,
                                                data.bid_amount,
                                                settings.ppaThreshold
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <table
                    className="w-full divide-y divide-brand-gray-300 font-secondary opacity-0 absolute top-0 left-0 pointer-events-none z-index-10"
                    style={{ fontSize: tableFontSize }}
                >
                    <thead className="sticky top-0 bg-white border-b-2 border-gray-200">
                        <tr>
                            <th
                                className="font-normal text-gray-800 px-1 text-center w-1/12"
                                style={{ fontSize: "0.85em" }}
                            >
                                BIDDER
                            </th>
                            <th
                                className="font-normal text-gray-800 px-1 text-center"
                                style={{ maxWidth: "13.5em", width: "13.5em", fontSize: "0.85em" }}
                            >
                                TRACTS
                            </th>
                            <th
                                className="font-normal text-gray-800 px-1 text-center"
                                style={{ fontSize: "0.85em" }}
                            >
                                BID AMOUNT
                            </th>
                            <th
                                className="font-normal text-gray-800 px-1 text-center"
                                style={{ fontSize: "0.85em" }}
                            >
                                ACRES
                            </th>
                            {(!settings || settings.showPricePerAcre) && (
                                <th
                                    className="font-normal text-gray-800 px-1 text-center"
                                    style={{ fontSize: "0.85em" }}
                                >
                                    PPA
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody
                        className="w-full bg-white divide-y divide-gray-200"
                        ref={allBidsRef}
                    >
                        {allBids.map((data, i) => {
                            return (
                                <tr
                                    key={data.bid_id}
                                    className={clsx(
                                        "borderless py-0.5",
                                        i % 2 && "bg-brand-gray-200"
                                    )}
                                >
                                    <td className="w-1/12 px-4">
                                        <div className="flex justify-between gap-2">
                                            <div className="flex items-center justify-center font-bold">
                                                #{data.bidder?.bidder_number}
                                            </div>
                                            <div
                                                style={{
                                                    width: "1em",
                                                    backgroundColor:
                                                        data.bidder
                                                            ?.bidder_color ??
                                                        "#ccc",
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td
                                        className="px-4 font-bold text-center break-words"
                                        style={{
                                            maxWidth: "13.5em",
                                            width: "13.5em",
                                            paddingTop: `${ROW_PADDING_EM}em`,
                                            paddingBottom: `${ROW_PADDING_EM}em`,
                                        }}
                                    >
                                        {data.bid_alias && (
                                            <span>{data.bid_alias} : </span>
                                        )}
                                        {formatSequential(data.bid_name)}
                                    </td>

                                    <td className="px-4 font-bold text-center">
                                        {formatUSD(data.bid_amount)}
                                    </td>
                                    <td className="px-4 font-bold text-center">
                                        {Math.round(
                                            countTractsAcres(data.tracts)
                                        )}
                                    </td>
                                    {(!settings ||
                                        settings.showPricePerAcre) && (
                                        <td className="px-4 font-bold text-center">
                                            {getPpaText(
                                                data.tracts,
                                                data.bid_amount,
                                                settings.ppaThreshold
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {shouldUsePagination && (
                <div
                    className="w-full flex items-center justify-center gap-1"
                    style={{ height: PAGINATION_BAR }}
                >
                    {Array.from({ length: numberOfPages }).map((_, i) => (
                        <div
                            key={i}
                            className={clsx(
                                "h-2 w-2 rounded-full",
                                i === bidPage - 1
                                    ? "bg-brand-gray-500"
                                    : "bg-brand-gray-300"
                            )}
                        />
                    ))}
                </div>
            )}
        </>
    );
}
